import { useMemo, useState } from "react";
import type { AddOn } from "@cliqsuite/db/schemas";
import type { CartLineSelection } from "../types";
import { formatMoney } from "../lib/format";

export function LineAddOnsModal({ lineName, addOns, initial, currency, onClose, onSave }: {
  lineName: string;
  addOns: AddOn[];
  initial: CartLineSelection[];
  currency: { code: string; precision: 0 | 2 | 3 };
  onClose: () => void;
  onSave: (selections: CartLineSelection[]) => void;
}) {
  const [selected, setSelected] = useState(() => new Set(initial.filter((s) => s.kind === "add_on").map((s) => s.addOnId).filter(Boolean)));
  const priorCustom = initial.find((s) => s.kind === "custom_charge");
  const [label, setLabel] = useState(priorCustom?.label ?? "");
  const [amount, setAmount] = useState(priorCustom ? String(priorCustom.priceDelta) : "");
  const fmt = (v: number) => formatMoney(v, { currencyCode: currency.code, precision: currency.precision });
  const configured = useMemo(() => addOns.filter((a) => a.is_active).sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name)), [addOns]);

  const save = () => {
    const next: CartLineSelection[] = configured.filter((a) => selected.has(a.id)).map((a) => ({
      optionId: null, addOnId: a.id, kind: "add_on", groupLabel: "Add-on", label: a.name,
      priceDelta: a.price, quantity: 1, ingredientId: a.ingredient_id, ingredientQty: a.ingredient_qty,
    }));
    const customAmount = Math.trunc(Number(amount));
    if (label.trim() && Number.isFinite(customAmount) && customAmount >= 0) next.push({
      optionId: null, addOnId: null, kind: "custom_charge", groupLabel: "Custom charge",
      label: label.trim(), priceDelta: Math.min(customAmount, 1_000_000), quantity: 1,
    });
    onSave(next);
  };

  return (
    <div className="pos-dim" onClick={onClose}>
      <section role="dialog" aria-modal="true" aria-labelledby="charge-t" className="pos-dialog pos-approve" onClick={(e) => e.stopPropagation()}>
        <span className="pos-approve-badge">
          <span className="ms" aria-hidden="true">add_card</span>
        </span>
        <h2 id="charge-t">Add-ons and charges</h2>
        <p>For {lineName}. A request that's not on the menu goes here too.</p>
        {configured.length > 0 && (
          <div className="pos-approve-field">
            <span>Add-ons</span>
            <div role="group" aria-label="Add-ons">
              {configured.map((a) => {
                const active = selected.has(a.id);
                return (
                  <button
                    key={a.id}
                    role="checkbox"
                    aria-checked={active}
                    className={active ? "on" : ""}
                    onClick={() =>
                      setSelected((old) => {
                        const n = new Set(old);
                        if (active) n.delete(a.id);
                        else n.add(a.id);
                        return n;
                      })
                    }
                  >
                    {active && <span className="ms" aria-hidden="true">check</span>}
                    {a.name} <span className="num" style={{ opacity: 0.7 }}>+{fmt(a.price)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <div className="pos-charge-fields">
          <label>
            <span>Charge</span>
            <span className="pos-input">
              <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={100} placeholder="e.g. Take-away cup" />
            </span>
          </label>
          <label>
            <span>Amount</span>
            <span className="pos-input">
              <span className="ms" aria-hidden="true" style={{ fontSize: 19, color: "var(--text2)" }}>payments</span>
              <input value={amount} onChange={(e) => setAmount(e.target.value.replace(/\D/g, ""))} inputMode="numeric" placeholder="0" />
            </span>
          </label>
        </div>
        <div className="dlg-row">
          <button className="pos-btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="pos-btn-coral" onClick={save}>
            Add to line
          </button>
        </div>
      </section>
    </div>
  );
}
